// src/components/IntakeModeSelection.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button
} from '@mui/material';
import { USE_ELEVENLABS_INTAKE, ELEVENLABS_CONFIG } from '../config/intakeConfig';

const ModeCard = ({ title, description, selected, onClick, disabled = false }) => {
  return (
    <Card
      onClick={!disabled ? onClick : undefined}
      sx={{
        mb: 2,
        border: selected ? '1px solid rgba(22, 127, 249, 0.6)' : '1px solid #EAECF0',
        borderRadius: '8px',
        backgroundColor: selected ? 'rgba(225, 239, 255, 0.6)' : '#FFFFFF',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: selected ? 'rgba(225, 239, 255, 0.6)' : '#F2F4F7'
        }
      }}
    >
      <CardContent sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color: '#101828',
            fontSize: '14px',
            lineHeight: '1.43em'
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#465366',
            fontSize: '13px',
            lineHeight: '1.38em'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const IntakeModeSelection = ({ onSelectMode, onClose }) => {
  // Resolve runtime flag with URL/localStorage/window fallbacks so it works even without rebuilds
  const runtimeFlag = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const qsv = qs.get('elevenlabs');
      if (qsv && ['1', 'true', 'yes', 'on'].includes(qsv.toLowerCase())) return true;
      if (typeof window !== 'undefined' && typeof window.USE_ELEVENLABS_INTAKE === 'boolean') return window.USE_ELEVENLABS_INTAKE;
      if (typeof window !== 'undefined' && window.localStorage?.getItem) {
        const ls = window.localStorage.getItem('USE_ELEVENLABS_INTAKE');
        if (ls === 'true') return true;
        if (ls === 'false') return false;
      }
    } catch (e) {}
    return !!USE_ELEVENLABS_INTAKE;
  })();

  const showVoiceMode = runtimeFlag === true || ELEVENLABS_CONFIG?.ENABLED === true;

  // Debug aid: verify what the modal sees
  try {
    console.log('🎙️ IntakeModeSelection flags:', {
      runtimeFlag,
      configEnabled: ELEVENLABS_CONFIG?.ENABLED,
      showVoiceMode
    });
  } catch (e) {}

  const [selectedMode, setSelectedMode] = useState(showVoiceMode ? 'elevenlabs' : 'topic'); // 'topic' | 'elevenlabs'

  const handleContinue = () => {
    if (!selectedMode) return;
    if (typeof onSelectMode === 'function') {
      onSelectMode(selectedMode);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: '90%',
        maxWidth: '700px',
        maxHeight: '80vh',
        zIndex: 900,
        overflow: 'auto',
        borderRadius: '12px',
        backgroundColor: '#FFFFFF'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: '24px 32px',
          borderBottom: '1px solid #EAECF0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            variant="h5"
            sx={{
              color: '#313131',
              fontWeight: 500,
              fontSize: '18px',
              lineHeight: '1.33em'
            }}
          >
            Choose Intake Mode
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#465366',
              fontSize: '14px',
              lineHeight: '1.43em'
            }}
          >
            Select how you want to conduct discovery for this role.
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ padding: '16px 32px', display: 'flex', flexDirection: 'column' }}>
        <ModeCard
          title="Mode A: Topic‑based Discovery"
          description="Guide the intake via structured topics (skills, titles, location, etc.)."
          selected={selectedMode === 'topic'}
          onClick={() => setSelectedMode('topic')}
        />

        {showVoiceMode && (
          <ModeCard
            title="Mode B: Conversational Discovery (ElevenLabs Agent)"
            description="Speak or type freely with a voice agent. We’ll capture the conversation and generate the search when you’re done."
            selected={selectedMode === 'elevenlabs'}
            onClick={() => setSelectedMode('elevenlabs')}
          />
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: '0px 32px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}
      >
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#465366'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{
            backgroundColor: '#167FF9',
            color: '#FFFFFF',
            textTransform: 'none',
            padding: '8px 12px',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#1068CC'
            }
          }}
        >
          Continue
        </Button>
      </Box>
    </Paper>
  );
};

export default IntakeModeSelection;
